import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, passesAccess, FullPageLoader } from "@livingsyncs/ui";
import type { RouteAccess } from "@livingsyncs/ui";

interface RouteGuardProps {
  access?: RouteAccess;
  children: ReactNode;
}

/**
 * Per-route access guard, auditor's own copy of frontend's `RouteGuard.tsx`
 * (frontend split epic Phase 6), same shape as apps/admin's. Trimmed: no
 * shiftRequired/shiftGate delegation — audit.portal.ts sets no shiftRequired
 * routes (a group-home staff on-shift concept), so there's nothing to
 * inject. Not moved to @livingsyncs/ui — same reason as every other
 * per-app RouteGuard copy: its useAuth() call would resolve to packages/ui's
 * own copy instead of this app's local mock target.
 */
export function RouteGuard({ access, children }: RouteGuardProps) {
  const { data: context, isLoading } = useAuth();

  const needsPermissionDecision = !!(
    access?.anyOf ||
    access?.allOf ||
    access?.resource
  );

  if (needsPermissionDecision) {
    if (isLoading || !context) {
      return <FullPageLoader />;
    }
    if (!passesAccess({ permissions: context.permissions }, access)) {
      return (
        <Navigate
          to="/no-access"
          replace
          state={{
            requiredPermissions: [
              ...(access?.anyOf ?? []),
              ...(access?.allOf ?? []),
              ...(access?.resource ? [`${access.resource}:read`] : []),
            ],
            from: window.location.pathname,
          }}
        />
      );
    }
  }

  return <>{children}</>;
}
